/*
  XYON Yotpo Reviews List Fetcher — case-study-aligned render.

  Aggregate summary (avg + stars + count) is server-rendered in Liquid
  by sections/product-reviews.liquid using Shopify review metafields.
  This script renders the review list (carousel + dots + see-all link)
  by calling the Yotpo Storefront API and injecting cards into the
  .xyr__list-mount container.

  Container element:
    <div
      class="xyr__list-mount"
      data-xyon-yotpo-reviews-list
      data-app-key="..."
      data-mode="product" | "site"
      data-product-id="123456"      (required when mode="product")
      data-per-page="24"
      data-see-all-label="..."      (optional)
      data-see-all-url="..."        (optional)
    ></div>

  Uses IntersectionObserver to defer the API call until the container
  is near the viewport. Loaded once globally via layout/theme.liquid.
*/
(() => {
  const API = {
    product: (appKey, productId) =>
      `https://api-cdn.yotpo.com/v1/widget/${appKey}/products/${productId}/reviews.json`,
    site: (appKey) =>
      `https://api-cdn.yotpo.com/products/${appKey}/yotpo_site_reviews/reviews.json`,
  };

  const STAR_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="12,3 14.5,9 21,9.5 16,14 17.5,21 12,17.5 6.5,21 8,14 3,9.5 9.5,9"/></svg>';

  function escapeHTML(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function fmtDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function starRow(score) {
    const filled = Math.round(score || 0);
    let html = '';
    for (let i = 1; i <= 5; i++) {
      html += '<span class="xyr-star' + (i <= filled ? ' is-filled' : '') + '">' + STAR_SVG + '</span>';
    }
    return html;
  }

  class XyonYotpoReviewsList {
    constructor(el) {
      this.el = el;
      this.appKey = el.dataset.appKey;
      // Strict mode validation: only 'site' or 'product'. Any other value
      // (typo, future mistake) falls through to 'product' so the productId
      // guard below catches the misconfiguration explicitly instead of
      // letting `undefined` reach the Yotpo API.
      this.mode = el.dataset.mode === 'site' ? 'site' : 'product';
      this.productId = el.dataset.productId;
      // Clamp perPage to a minimum of 1. Liquid `default: 24` does not
      // replace 0, so a merchant setting per_page=0 would otherwise send
      // `per_page=0` to Yotpo and get an empty result set.
      const parsedPerPage = parseInt(el.dataset.perPage || '24', 10);
      this.perPage = Math.max(1, isNaN(parsedPerPage) ? 24 : parsedPerPage);
      this.seeAllLabel = el.dataset.seeAllLabel || '';
      this.seeAllUrl = el.dataset.seeAllUrl || '';
      this.reviews = [];
      this.totalReviews = 0;
      this.controller = null;
      this.resizeTimer = null;
      this.boundResize = null;

      // Render the shell up front so the `:empty { min-height: 480px }` CSS
      // rule no longer reserves a blank 480px gap when we early-return
      // below. Any error state then renders inside the shell.
      this.renderShell();

      if (!this.appKey) {
        console.warn('[xyr] Missing data-app-key on', el);
        this.renderError('Reviews configuration missing.');
        return;
      }
      if (this.mode === 'product' && !this.productId) {
        console.warn('[xyr] Missing data-product-id for product mode on', el);
        this.renderError('Reviews configuration missing.');
        return;
      }

      this.fetchReviews();
    }

    endpoint() {
      return this.mode === 'site'
        ? API.site(this.appKey)
        : API.product(this.appKey, this.productId);
    }

    async fetchReviews() {
      const params = new URLSearchParams({
        per_page: String(this.perPage),
        page: '1',
        sort: 'date',
        direction: 'desc',
      });
      // AbortController + isConnected checks: if the user navigates away
      // (or the section is unloaded by the theme editor) while this fetch
      // is in flight, we cancel the request and skip mutating a detached
      // DOM subtree.
      this.controller = new AbortController();
      this.setLoading(true);
      try {
        const r = await fetch(this.endpoint() + '?' + params.toString(), {
          headers: { Accept: 'application/json' },
          signal: this.controller.signal,
        });
        if (!this.el.isConnected) return;
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const json = await r.json();
        if (!this.el.isConnected) return;
        const data = (json && json.response) || {};
        if (data.bottomline) {
          this.totalReviews = data.bottomline.total_review || 0;
        }
        // Surface reviews with customer photos first. Stable sort preserves
        // the date-desc order within each group (photos / no-photos), so the
        // carousel still feels chronological inside each band.
        const all = data.reviews || [];
        this.reviews = all.slice().sort((a, b) => {
          const aHas = (a.images_data && a.images_data.length > 0) ? 1 : 0;
          const bHas = (b.images_data && b.images_data.length > 0) ? 1 : 0;
          return bHas - aHas;
        });
        this.render();
      } catch (e) {
        if (e.name === 'AbortError') return;
        if (!this.el.isConnected) return;
        console.error('[xyr] Fetch failed:', e);
        this.renderError();
      } finally {
        if (this.el.isConnected) this.setLoading(false);
      }
    }

    renderShell() {
      this.el.innerHTML = [
        '<div class="xyr__track" role="region" aria-label="Customer reviews">',
        '  <div class="xyr__row" role="list"></div>',
        '</div>',
        '<div class="xyr__footer">',
        '  <div class="xyr__dots-viewport">',
        '    <div class="xyr__dots" role="group" aria-label="Reviews pagination"></div>',
        '  </div>',
        '  <a class="xyr__see-all" hidden></a>',
        '</div>',
      ].join('\n');

      this.trackEl = this.el.querySelector('.xyr__track');
      this.rowEl = this.el.querySelector('.xyr__row');
      this.dotsViewportEl = this.el.querySelector('.xyr__dots-viewport');
      this.dotsEl = this.el.querySelector('.xyr__dots');
      this.seeAllEl = this.el.querySelector('.xyr__see-all');

      if (this.seeAllLabel && this.seeAllUrl) {
        this.seeAllEl.textContent = this.seeAllLabel;
        this.seeAllEl.href = this.seeAllUrl;
        this.seeAllEl.hidden = false;
      }

      this.trackEl.addEventListener('scroll', () => this.updateActiveDot(), { passive: true });
      this.dotsEl.addEventListener('click', (e) => {
        const dot = e.target.closest('.xyr__dot');
        if (!dot) return;
        const idx = parseInt(dot.dataset.idx, 10);
        if (!isNaN(idx)) this.scrollToIndex(idx);
      });

      // Recompute dot positions on window resize. Card widths follow
      // min(80vw, 360px) — resizing changes the step, the viewport, and
      // the centered-active illusion offset. Debounce so we don't run on
      // every resize tick.
      this.boundResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          if (this.el.isConnected) this.updateActiveDot();
        }, 150);
      };
      window.addEventListener('resize', this.boundResize, { passive: true });
    }

    render() {
      if (this.reviews.length === 0) {
        this.rowEl.innerHTML = '<p class="xyr__empty">No reviews yet.</p>';
        this.dotsEl.innerHTML = '';
        return;
      }
      this.rowEl.innerHTML = this.reviews.map((r) => this.renderReviewHTML(r)).join('');
      this.renderDots();
      // Seed distance attributes so the first paint shows the pyramid
      // (active dot biggest, neighbors fading) instead of all dots
      // equal-sized until the user scrolls.
      this.updateActiveDot();
    }

    renderReviewHTML(review) {
      const name = escapeHTML((review.user && review.user.display_name) || 'Anonymous');
      const verifiedHTML = review.verified_buyer
        ? '<span class="xyr-card__sep" aria-hidden="true">·</span><span class="xyr-card__verified">Verified buyer</span>'
        : '';
      const date = fmtDate(review.created_at);
      const score = review.score || 0;
      const title = escapeHTML(review.title || '');
      const rawContent = (review.content || '').trim();
      // Preserve paragraph breaks: split on `\n\s*\n+`, escape and collapse
      // intra-paragraph whitespace inside each paragraph, then join with
      // `<br><br>`. Without this, multi-paragraph reviews collapse to one
      // continuous line because escapeHTML doesn't touch newlines.
      const contentText = rawContent
        .split(/\n\s*\n+/)
        .map((p) => escapeHTML(p.replace(/\s+/g, ' ').trim()))
        .filter(Boolean)
        .join('<br><br>');

      const photoCells = (review.images_data || []).slice(0, 2).map((img) => {
        const thumb = escapeHTML(img.thumb_url || img.original_url || '');
        const orig = escapeHTML(img.original_url || img.thumb_url || '');
        if (!thumb) return '';
        return '<a class="xyr-card__photo" href="' + orig + '" target="_blank" rel="noopener noreferrer" aria-label="Open review photo in new tab"><img src="' + thumb + '" alt="" loading="lazy"></a>';
      }).filter(Boolean).join('');

      return [
        '<article class="xyr-card" role="listitem">',
        photoCells ? '  <div class="xyr-card__photos">' + photoCells + '</div>' : '',
        '  <div class="xyr-card__identity">',
        '    <span class="xyr-card__name">' + name + '</span>',
        '    ' + verifiedHTML,
        '  </div>',
        '  <div class="xyr-card__info">',
        '    <div>',
        '      <div class="xyr-card__info-label">Rating</div>',
        '      <div class="xyr-card__info-value xyr-card__stars" aria-label="' + score + ' out of 5">' + starRow(score) + '</div>',
        '    </div>',
        '    <div>',
        '      <div class="xyr-card__info-label">Date</div>',
        '      <div class="xyr-card__info-value">' + escapeHTML(date) + '</div>',
        '    </div>',
        '  </div>',
        title ? '  <h3 class="xyr-card__title">' + title + '</h3>' : '',
        contentText ? '  <blockquote class="xyr-card__quote">&ldquo;' + contentText + '&rdquo;</blockquote>' : '',
        '</article>',
      ].filter(Boolean).join('\n');
    }

    renderDots() {
      const count = this.reviews.length;
      if (count <= 1) {
        this.dotsEl.innerHTML = '';
        return;
      }
      let html = '';
      for (let i = 0; i < count; i++) {
        html += '<button type="button" class="xyr__dot" data-idx="' + i + '" aria-label="Go to review ' + (i + 1) + '" aria-current="' + (i === 0 ? 'true' : 'false') + '"></button>';
      }
      this.dotsEl.innerHTML = html;
    }

    scrollToIndex(idx) {
      const cards = this.rowEl.querySelectorAll('.xyr-card');
      const card = cards[idx];
      if (!card) return;
      const left = card.offsetLeft - this.rowEl.offsetLeft;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.trackEl.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    updateActiveDot() {
      const cards = this.rowEl.querySelectorAll('.xyr-card');
      if (cards.length === 0) return;
      const firstCard = cards[0];
      const gap = parseFloat(getComputedStyle(this.rowEl).columnGap) || 0;
      const step = firstCard.offsetWidth + gap;
      if (!step) return;
      const idx = Math.max(0, Math.min(cards.length - 1, Math.round(this.trackEl.scrollLeft / step)));
      const dots = this.dotsEl.querySelectorAll('.xyr__dot');
      dots.forEach((d, i) => {
        d.setAttribute('aria-current', i === idx ? 'true' : 'false');
      });

      // Centered-active illusion — translate the dots strip so the
      // active dot sits in the middle of the viewport. Clamp the
      // translate so we don't overscroll past the strip's edges (so
      // at the start of the carousel the first few dots sit at the
      // left of the viewport, and at the end the last few sit at the
      // right, with the active centered everywhere in between).
      const activeDot = dots[idx];
      if (!activeDot || !this.dotsViewportEl) return;
      const viewportW = this.dotsViewportEl.offsetWidth;
      const stripW = this.dotsEl.scrollWidth;
      if (!viewportW || !stripW) return;
      const desired = viewportW / 2 - activeDot.offsetLeft - activeDot.offsetWidth / 2;
      const minOffset = Math.min(0, viewportW - stripW);
      const clamped = Math.max(minOffset, Math.min(0, desired));
      this.dotsEl.style.transform = 'translateX(' + clamped + 'px)';
    }

    renderError(message) {
      const safeMessage = message
        ? escapeHTML(message)
        : 'Unable to load reviews. Please refresh the page.';
      this.rowEl.innerHTML = '<p class="xyr__error">' + safeMessage + '</p>';
      this.dotsEl.innerHTML = '';
    }

    setLoading(loading) {
      this.el.dataset.loading = loading ? 'true' : 'false';
    }
  }

  // Single module-scope IntersectionObserver. Reused across all init()
  // calls so observers don't leak when init runs again (e.g. via the
  // shopify:section:load event in the theme editor). The constructor is
  // wrapped in try/catch and the xyrInitialized flag is only set on
  // success — so a constructor throw leaves no flag, allowing a future
  // init() to retry.
  const io = ('IntersectionObserver' in window)
    ? new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          try {
            new XyonYotpoReviewsList(entry.target);
            entry.target.dataset.xyrInitialized = 'true';
          } catch (e) {
            console.error('[xyr] Constructor failed:', e);
          }
        });
      }, { rootMargin: '400px' })
    : null;

  function init() {
    const targets = document.querySelectorAll('[data-xyon-yotpo-reviews-list]:not([data-xyr-initialized])');
    if (targets.length === 0) return;

    if (io) {
      targets.forEach((el) => io.observe(el));
    } else {
      // No IntersectionObserver support: instantiate eagerly with the
      // same try/catch envelope.
      targets.forEach((el) => {
        try {
          new XyonYotpoReviewsList(el);
          el.dataset.xyrInitialized = 'true';
        } catch (e) {
          console.error('[xyr] Constructor failed:', e);
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-init for sections added or re-rendered live in the Shopify theme
  // editor. Without this, new mounts dragged in via the customizer never
  // attach to the IntersectionObserver and stay blank until full reload.
  document.addEventListener('shopify:section:load', init);
})();
